import { useRef, useEffect, useState } from "react";
import AdvancedTaskDto from "../../../../types/dtos/forms/AdvancedTaskDto";
import TaskManagementConfigDto from "../../../../types/dtos/forms/TaskManagementConfigDto";
import { t } from "i18next";
import TaskStatusBadge from "./TaskStatusBadge";
import cx from "classnames";
import { UserContextInterface } from "../../../../state/UserContext";
import { Badge, DropDownMenu, HighlightDot, Tooltip } from "../../../common";
import { TaskManagementDropDownMenuArgs } from "../../../../types/tasks/TaskManagementDropDownMenuArgs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faCheck,
  faXmark,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { ClientTaskType } from "../../../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import { advancedTaskHelper, userDetailsHelper } from "../../../../helpers";
import { getTaskWeightingBadgeText } from "../../../../helpers/taskWeightingHelper";
import EnforcedCommentsSection from "./EnforcedCommentsSection";
import { FormType } from "../../../../types/forms";
import { AdvancedTaskStatus } from "../../../../types/dtos/tasks/advanced-tasks/AdvancedTaskStatus";
import AdvancedTaskEnforcedCommentDto from "../../../../types/dtos/forms/AdvancedTaskEnforcedCommentDto";
import AvatarWithIcon from "../../../common/AvatarWithIcon";
import { UserBasicDetailsDto } from "../../../../types/dtos/generic";
import AdvancedTaskTypeSwapabilityDto from "../../../../types/dtos/forms/AdvancedTaskTypeSwapabilityDto";

interface AdvancedTaskDesktopItemProps {
  userContext: UserContextInterface;
  task: AdvancedTaskDto;
  taskType: ClientTaskType | undefined;
  config: TaskManagementConfigDto;
  isCancelled?: boolean;
  isReadOnly: boolean;
  formType: FormType;
  displayRatingColumn: boolean;
  columnCount: number;
  loggedInUserId: number;
  isLoggedInUserTheSubjectUser: boolean;
  subjectUser: UserBasicDetailsDto | undefined;
  nonSubjectUser: UserBasicDetailsDto | undefined;
  participants: UserBasicDetailsDto[];
  taskTypeIcon: IconDefinition | null;
  onDropdownMenuItemClick: (userTaskId: string, action: string) => void;
  onViewButtonClick: (userTaskId: string, snapshotDate: Date) => void;
  isLastInSet?: boolean;
  showExtraColumnsBecauseOfEnforcedComments: boolean;
  isTaskTypeIdSwapable?: boolean;
  swapabilityInfo?: AdvancedTaskTypeSwapabilityDto;
  isReviewQuestion?: boolean;
  isSettingQuestion?: boolean;
  isDualPrepPlanningForm?: boolean;
}

interface EnforcedCommentsSectionWrapperProps {
  task: AdvancedTaskDto;
  taskType: ClientTaskType;
  formType: FormType;
  status: AdvancedTaskStatus;
  details: AdvancedTaskEnforcedCommentDto;
  contentClassName?: string;
  loggedInUserId: number;
  isLoggedInUserTheSubjectUser: boolean;
  subjectUsersName: string;
  nonSubjectUsersName: string;
  onItemAction: (userTaskId: string, action: string) => void;
}

function EnforcedCommentsSectionWrapper(
  props: EnforcedCommentsSectionWrapperProps
) {
  const [hasContent, setHasContent] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setHasContent(contentRef.current.children.length > 0);
    }
  }, []);

  if (!hasContent) {
    return null;
  }

  return (
    <div className="bg-white border-b">
      <div className="col-span-full">
        <div ref={contentRef}>
          <EnforcedCommentsSection {...props} />
        </div>
      </div>
    </div>
  );
}

function AdvancedTaskDesktopItem({
  userContext,
  task,
  taskType,
  config,
  isCancelled = false,
  isReadOnly,
  formType,
  displayRatingColumn,
  columnCount,
  loggedInUserId,
  isLoggedInUserTheSubjectUser,
  subjectUser,
  nonSubjectUser,
  participants,
  taskTypeIcon,
  onDropdownMenuItemClick,
  onViewButtonClick,
  isLastInSet = false,
  showExtraColumnsBecauseOfEnforcedComments,
  isTaskTypeIdSwapable = false,
  swapabilityInfo,
  isReviewQuestion = false,
  isSettingQuestion = false,
  isDualPrepPlanningForm = false,
}: AdvancedTaskDesktopItemProps) {
  /// Show enforced comments section if its enabled and isn't for cancelled tasks.
  /// Read-only / view-only questions must not chase comments the popup will refuse.
  const showEnforceCommentsSection =
    !isReadOnly &&
    config.enforceComments == true &&
    task.enforcedComments != null &&
    isCancelled == false &&
    (config.answerSetGuidId == null ||
      task.createdInAnswerSetGuidId != config.answerSetGuidId);

  const ratingIsClickable = !isCancelled && !isReadOnly;

  const onDropDownItemClick = (index: number, action: string) => {
    onDropdownMenuItemClick(task.taskId, action);
  };

  const onRatingClick = () => {
    if (ratingIsClickable) {
      // Check if rating should be clickable based on the same logic as TaskRatingDisplay
      if (
        !advancedTaskHelper.canUserInteractWithRating(
          task,
          showExtraColumnsBecauseOfEnforcedComments,
          isLoggedInUserTheSubjectUser
        )
      ) {
        return; // Don't handle the click if rating is not clickable
      }

      if (task.rating != null) {
        // Task could come from either ReviewForm OR CloseForm, so try to direct to the most relevant
        if (task.hasReviewFormResponse) {
          onDropdownMenuItemClick(
            task.taskId,
            TaskManagementDropDownMenuArgs.ReviewTask
          );
        } else {
          onDropdownMenuItemClick(
            task.taskId,
            TaskManagementDropDownMenuArgs.CompleteTask
          );
        }
      } else if (task.status === "COMPLETED") {
        const ratingAction = advancedTaskHelper.getCompletedTaskRatingAction(
          task,
          isLoggedInUserTheSubjectUser
        );
        if (ratingAction) {
          onDropdownMenuItemClick(task.taskId, ratingAction);
        }
      }
    }
  };

  const onViewClickHandler = () => {
    if (task.snapshotDate != null) {
      onViewButtonClick(task.taskId, task.snapshotDate);
    } else {
      // Live read-only rows (e.g. view-only planning) have no snapshot; open details.
      onDropDownItemClick(0, TaskManagementDropDownMenuArgs.ViewDetails);
    }
  };

  const viewTaskDetailsPopup = () => {
    onDropDownItemClick(0, TaskManagementDropDownMenuArgs.ViewDetails);
  };

  const btnDropDownContents = (
    <div>
      <button
        id="adv-task-table-item-option"
        className="text-[#5d5d5d] bg-[#F5F5F5] hover:bg-gray-200 font-medium rounded-sm py-1 px-4 border border-gray-300 text-xs w-full lg:inline-block lg:w-auto"
      >
        {t("ManagerDashboard.Common.Actions")}
        <FontAwesomeIcon icon={faChevronDown} className="pl-2" />
      </button>
    </div>
  );

  const nonSubjectUsersName = userDetailsHelper.getDisplayName(
    nonSubjectUser?.userId!,
    participants
  );
  const subjectUsersName = userDetailsHelper.getDisplayName(
    subjectUser?.userId!,
    participants
  );

  const weightingBadgeText = getTaskWeightingBadgeText(
    taskType,
    task.weightingValue
  );

  const isCompletedButNeedsReviewingStill =
    task.status === "COMPLETED" &&
    taskType?.reviewFormId &&
    !task.hasReviewFormResponse;

  return (
    <>
      <div
        className={cx(
          "bg-white",
          showEnforceCommentsSection == false ? "border-b" : "",
          isLastInSet && "rounded-b-lg"
        )}
      >
        <div className="flex">
          <div
            className={cx(
              "flex-1 px-6 font-medium whitespace-nowrap sm:whitespace-normal flex flex-row",
              isCancelled ? "line-through text-gray-300" : "text-gray-900",
              showEnforceCommentsSection ? "py-2" : "py-4"
            )}
          >
            <div className="flex items-center gap-2">
              {advancedTaskHelper.taskIsHighlightedForUser(
                userContext,
                task,
                isReadOnly
              ) && <HighlightDot containerClassNames="flex-shrink-0" />}
              <button
                className="hover:cursor-pointer text-left flex items-center gap-2"
                onClick={viewTaskDetailsPopup}
              >
                {taskTypeIcon && (
                  <FontAwesomeIcon
                    icon={taskTypeIcon}
                    className="flex-shrink-0"
                  />
                )}
                <span className="whitespace-normal">{task.title}</span>
              </button>
              {weightingBadgeText && (
                <Badge
                  text={weightingBadgeText}
                  backgroundColourClassName="bg-gray-200/75"
                  textColourClassName="text-gray-600"
                  borderRadiusClassName="rounded-full"
                  marginClassName=""
                />
              )}
            </div>
          </div>
          <div className="w-32 text-center px-6 py-4">
            <TaskStatusBadge
              status={task.status}
              isNewlyCreatedInThisJourney={task.isNewlyCreatedInThisJourney}
              isOverdue={task.isOverdue}
            />
          </div>

          {showExtraColumnsBecauseOfEnforcedComments && !isReadOnly && (
            <div className="w-32 text-center px-6 py-4">
              {formType === "JOURNEY" && (
                <>
                  {task.status === "CANCELLED" ||
                  task.enforcedComments.areOwnerCountConditionsSatisfied ? (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-green-500"
                    />
                  ) : (
                    <FontAwesomeIcon icon={faXmark} className="text-red-500" />
                  )}
                </>
              )}
              {formType === "COLLAB-DOC" && (
                <>
                  {task.status === "CANCELLED" ? (
                    <span>N/A</span>
                  ) : (
                    <div className="grid grid-cols-2 gap-1">
                      {/* Show icons for subject and non-subject users */}
                      {subjectUser && (
                        <AvatarWithIcon
                          icon={
                            task.enforcedComments
                              .areOwnerCountConditionsSatisfied
                              ? faCheck
                              : faXmark
                          }
                          iconColourClassName={
                            task.enforcedComments
                              .areOwnerCountConditionsSatisfied
                              ? "text-green-500"
                              : "text-red-500"
                          }
                          imageUrl={subjectUser.profileImageUrl}
                          userFullName={subjectUser.fullName}
                          userInitials={subjectUser.initials}
                          size={8}
                          initialsTextClassName="text-xs"
                        />
                      )}

                      {nonSubjectUser && (
                        <AvatarWithIcon
                          icon={
                            task.enforcedComments
                              .areManagerRoleCountConditionsSatisfied
                              ? faCheck
                              : faXmark
                          }
                          iconColourClassName={
                            task.enforcedComments
                              .areManagerRoleCountConditionsSatisfied
                              ? "text-green-500"
                              : "text-red-500"
                          }
                          imageUrl={nonSubjectUser.profileImageUrl}
                          userFullName={nonSubjectUser.fullName}
                          userInitials={nonSubjectUser.initials}
                          size={8}
                          initialsTextClassName="text-xs"
                        />
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {displayRatingColumn && (
            <div onClick={onRatingClick} className="w-24 text-center px-6 py-4">
              {isCancelled && <>N/A</>}
              {!isCancelled && (
                <TaskRatingDisplay
                  task={task}
                  config={config}
                  ratingIsClickable={ratingIsClickable}
                  showExtraColumnsBecauseOfEnforcedComments={
                    showExtraColumnsBecauseOfEnforcedComments
                  }
                  isLoggedInUserTheSubjectUser={isLoggedInUserTheSubjectUser}
                />
              )}
            </div>
          )}

          {false &&
            showExtraColumnsBecauseOfEnforcedComments &&
            !isReadOnly &&
            formType === "COLLAB-DOC" && (
              <div className="w-32 text-center px-6 py-4">
                {task.status === "CANCELLED" && (
                  <span className="text-gray-500 text-sm">N/A</span>
                )}

                {/* Employee: */}
                {task.status !== "CANCELLED" &&
                  isLoggedInUserTheSubjectUser && (
                    <>
                      {task.enforcedComments.areOwnerCountConditionsSatisfied &&
                      !isCompletedButNeedsReviewingStill ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-green-500"
                        />
                      ) : (
                        <Tooltip
                          triggerElement={
                            <span>
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="text-red-500"
                              />
                            </span>
                          }
                          content={
                            !task.enforcedComments
                              .areOwnerCountConditionsSatisfied
                              ? "Comment required"
                              : task.enforcedComments
                                  .areOwnerCountConditionsSatisfied &&
                                isCompletedButNeedsReviewingStill
                              ? "Please rate/review"
                              : "Not reviewed"
                          }
                        />
                      )}
                    </>
                  )}
                {/* Manager: */}
                {task.status !== "CANCELLED" &&
                  !isLoggedInUserTheSubjectUser && (
                    <>
                      {task.enforcedComments
                        .areManagerRoleCountConditionsSatisfied &&
                      !isCompletedButNeedsReviewingStill ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-green-500"
                        />
                      ) : (
                        <Tooltip
                          triggerElement={
                            <span>
                              <FontAwesomeIcon
                                icon={faXmark}
                                className="text-red-500"
                              />
                            </span>
                          }
                          content={
                            !task.enforcedComments
                              .areManagerRoleCountConditionsSatisfied
                              ? "Comment required"
                              : task.enforcedComments
                                  .areManagerRoleCountConditionsSatisfied &&
                                isCompletedButNeedsReviewingStill
                              ? "Please rate/review"
                              : "Not reviewed"
                          }
                        />
                      )}
                    </>
                  )}
              </div>
            )}

          {/* Not Read-only - Show actions button */}
          {!isReadOnly && (
            <div className="w-40 text-center px-6 py-4">
              <DropDownMenu
                eventType="EVENT"
                items={advancedTaskHelper.getDropDownMenuOptions(
                  task,
                  taskType,
                  formType,
                  showEnforceCommentsSection,
                  config.questionMode,
                  isLoggedInUserTheSubjectUser,
                  subjectUsersName,
                  isTaskTypeIdSwapable,
                  swapabilityInfo,
                  isReviewQuestion,
                  isSettingQuestion,
                  isDualPrepPlanningForm
                )}
                onItemClick={onDropDownItemClick}
                customButtonContents={btnDropDownContents}
                menuButtonClassName="cursor-pointer"
                renderMenuInPortal
              />
            </div>
          )}
          {/* Read-only - Show view button */}
          {isReadOnly && (
            <div className="w-40 text-center px-6 py-4">
              <button
                className="text-[#5d5d5d] bg-[#F5F5F5] hover:bg-gray-200 font-medium rounded-sm py-1 px-4 border border-gray-300 text-xs w-full lg:inline-block lg:w-auto"
                onClick={onViewClickHandler}
              >
                {t("Common.View")}
              </button>
            </div>
          )}
        </div>
      </div>

      {showEnforceCommentsSection && (
        <EnforcedCommentsSectionWrapper
          task={task}
          taskType={taskType!}
          formType={formType}
          status={task.status}
          details={task.enforcedComments}
          contentClassName="pl-6 pb-2"
          loggedInUserId={loggedInUserId}
          isLoggedInUserTheSubjectUser={isLoggedInUserTheSubjectUser}
          subjectUsersName={subjectUsersName}
          nonSubjectUsersName={nonSubjectUsersName}
          onItemAction={onDropdownMenuItemClick}
        />
      )}
    </>
  );
}

interface TaskRatingDisplayProps {
  task: AdvancedTaskDto;
  config: TaskManagementConfigDto;
  ratingIsClickable: boolean;
  showExtraColumnsBecauseOfEnforcedComments: boolean;
  isLoggedInUserTheSubjectUser: boolean;
}

/** What renders in the rating column */
const TaskRatingDisplay: React.FC<TaskRatingDisplayProps> = ({
  task,
  config,
  ratingIsClickable,
  showExtraColumnsBecauseOfEnforcedComments,
  isLoggedInUserTheSubjectUser,
}) => {
  const shouldShowRatePrompt =
    task.status === "COMPLETED" &&
    task.isNewlyCreatedInThisJourney == false &&
    (!showExtraColumnsBecauseOfEnforcedComments ||
      (task.enforcedComments != null &&
        task.enforcedComments.areOwnerCountConditionsSatisfied &&
        task.enforcedComments.areManagerRoleCountConditionsSatisfied));

  const isClickable =
    ratingIsClickable &&
    advancedTaskHelper.canUserInteractWithRating(
      task,
      showExtraColumnsBecauseOfEnforcedComments,
      isLoggedInUserTheSubjectUser
    );

  if (task.rating != null) {
    return (
      <span className="hover:cursor-pointer hover:underline">
        {`${task.rating}/${config.ratingQuestionMaxValue}`}
      </span>
    );
  }

  return (
    <span className={isClickable ? "hover:cursor-pointer hover:underline" : ""}>
      {shouldShowRatePrompt ? (
        <span className="text-sm">
          {isLoggedInUserTheSubjectUser && task.enforcedComments != null ? (
            !task.enforcedComments.areOwnerCountConditionsSatisfied ? (
              <>
                Please
                <br />
                comment
              </>
            ) : !task.enforcedComments
                .areManagerRoleCountConditionsSatisfied ? (
              "-"
            ) : (
              <>
                Please
                <br />
                rate
              </>
            )
          ) : task.enforcedComments != null &&
            !task.enforcedComments.areManagerRoleCountConditionsSatisfied ? (
            <>
              Please
              <br />
              comment
            </>
          ) : (
            <>
              Please
              <br />
              rate
            </>
          )}
        </span>
      ) : (
        <span>-</span>
      )}
    </span>
  );
};

export default AdvancedTaskDesktopItem;
